import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Ruler, Tag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function CategoriesManagement() {
  // --- Categories ---
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  // --- Order Units ---
  const [orderUnits, setOrderUnits] = useState([]);
  const [unitLoading, setUnitLoading] = useState(true);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [newUnitName, setNewUnitName] = useState("");
  const [newUnitOrder, setNewUnitOrder] = useState(0);

  useEffect(() => {
    fetchCategories();
    fetchOrderUnits();
  }, []);

  const fetchCategories = async () => {
    setCatLoading(true);
    try {
      const r = await axios.get(`${API}/categories`, getAuthHeaders());
      setCategories(r.data);
    } catch (e) {
      toast.error("カテゴリの取得に失敗しました");
    } finally {
      setCatLoading(false);
    }
  };

  const fetchOrderUnits = async () => {
    setUnitLoading(true);
    try {
      const r = await axios.get(`${API}/order-units`, getAuthHeaders());
      setOrderUnits(r.data);
    } catch (e) {
      toast.error("発注単位の取得に失敗しました");
    } finally {
      setUnitLoading(false);
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCatName.trim()) return;
    try {
      await axios.post(`${API}/categories`, { name: newCatName.trim() }, getAuthHeaders());
      toast.success("カテゴリを追加しました");
      setNewCatName("");
      setCatDialogOpen(false);
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.detail || "追加に失敗しました");
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("このカテゴリを削除しますか？")) return;
    try {
      await axios.delete(`${API}/categories/${id}`, getAuthHeaders());
      toast.success("削除しました");
      fetchCategories();
    } catch (e) {
      toast.error("削除に失敗しました");
    }
  };

  const addUnit = async (e) => {
    e.preventDefault();
    if (!newUnitName.trim()) return;
    try {
      await axios.post(
        `${API}/order-units`,
        { name: newUnitName.trim(), sort_order: parseInt(newUnitOrder) || 0 },
        getAuthHeaders()
      );
      toast.success("発注単位を追加しました");
      setNewUnitName("");
      setNewUnitOrder(0);
      setUnitDialogOpen(false);
      fetchOrderUnits();
    } catch (e) {
      toast.error(e.response?.data?.detail || "追加に失敗しました");
    }
  };

  const deleteUnit = async (id) => {
    if (!window.confirm("この発注単位を削除しますか？")) return;
    try {
      await axios.delete(`${API}/order-units/${id}`, getAuthHeaders());
      toast.success("削除しました");
      fetchOrderUnits();
    } catch (e) {
      toast.error("削除に失敗しました");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold font-chivo tracking-tight text-slate-800">
          カテゴリ管理
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          商品カテゴリと発注単位のマスタを管理します
        </p>
      </div>

      <Tabs defaultValue="categories" className="w-full">
        <TabsList className="bg-slate-100 rounded-sm">
          <TabsTrigger
            value="categories"
            data-testid="tab-categories"
            className="flex items-center gap-1.5"
          >
            <Tag className="h-4 w-4" />
            カテゴリ一覧
          </TabsTrigger>
          <TabsTrigger
            value="units"
            data-testid="tab-units"
            className="flex items-center gap-1.5"
          >
            <Ruler className="h-4 w-4" />
            発注単位マスタ
          </TabsTrigger>
        </TabsList>

        {/* カテゴリ一覧 タブ */}
        <TabsContent value="categories" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-category-button"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  カテゴリを追加
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>新しいカテゴリを追加</DialogTitle>
                </DialogHeader>
                <form onSubmit={addCategory} className="space-y-3">
                  <div>
                    <Label htmlFor="cat-name">カテゴリ名</Label>
                    <Input
                      id="cat-name"
                      data-testid="category-name-input"
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      className="mt-1 h-9 rounded-sm"
                      placeholder="例: 野菜, 果物"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="category-submit-button"
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                  >
                    追加
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {catLoading ? (
            <div className="text-center py-12 text-slate-500">読み込み中...</div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase">
                      カテゴリ名
                    </th>
                    <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-28">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr
                      key={cat.id}
                      className="border-b border-slate-100 hover:bg-slate-50/50"
                      data-testid={`category-row-${cat.id}`}
                    >
                      <td className="p-2 px-4 align-middle">{cat.name}</td>
                      <td className="p-2 px-4 align-middle text-center">
                        <Button
                          onClick={() => deleteCategory(cat.id)}
                          data-testid={`delete-category-${cat.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {categories.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  カテゴリがありません
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* 発注単位マスタ タブ */}
        <TabsContent value="units" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-xs text-slate-500">
              ※ 発注時のドロップダウンに表示される単位の一覧を管理します（個・本・kg 等）
            </p>
            <Dialog open={unitDialogOpen} onOpenChange={setUnitDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-unit-button"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  発注単位を追加
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>新しい発注単位を追加</DialogTitle>
                </DialogHeader>
                <form onSubmit={addUnit} className="space-y-3">
                  <div>
                    <Label htmlFor="unit-name">単位名</Label>
                    <Input
                      id="unit-name"
                      data-testid="unit-name-input"
                      value={newUnitName}
                      onChange={(e) => setNewUnitName(e.target.value)}
                      className="mt-1 h-9 rounded-sm"
                      placeholder="例: 個, 本, kg, 束"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="unit-order">並び順</Label>
                    <Input
                      id="unit-order"
                      data-testid="unit-sort-order-input"
                      type="number"
                      value={newUnitOrder}
                      onChange={(e) => setNewUnitOrder(e.target.value)}
                      className="mt-1 h-9 rounded-sm"
                      placeholder="0"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="unit-submit-button"
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                  >
                    追加
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {unitLoading ? (
            <div className="text-center py-12 text-slate-500">読み込み中...</div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase w-24">
                      並び順
                    </th>
                    <th className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase">
                      単位名
                    </th>
                    <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-28">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orderUnits.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-slate-100 hover:bg-slate-50/50"
                      data-testid={`unit-row-${u.id}`}
                    >
                      <td className="p-2 px-4 align-middle font-mono text-slate-500">
                        {u.sort_order ?? 0}
                      </td>
                      <td className="p-2 px-4 align-middle font-medium">{u.name}</td>
                      <td className="p-2 px-4 align-middle text-center">
                        <Button
                          onClick={() => deleteUnit(u.id)}
                          data-testid={`delete-unit-${u.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {orderUnits.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  発注単位がありません
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
